import type Decimal from 'decimal.js';
import { D } from '../internal/decimal.js';
import { type Cents, cents } from '../money/index.js';
import { qty } from '../quantity/index.js';
import { type Uom, type UomConversion, convert } from '../uom/index.js';

/**
 * Standard cost roll-up (vault Core Model "Costing: standard vs actual", change 10:
 * "unlimited nesting with cost rolled up identically everywhere"; plan §12.1 step 5, §14
 * M5). **One function** for the import plan, the Admin's cost view, the replay and (M7)
 * feasibility, so a product costs the same wherever it is shown.
 *
 * A product's standard cost per **base unit** is, in order:
 *  1. a person's cost (`fixed`: a decision in the import review, or set in the Admin);
 *  2. its recipe: Σ (component cost × line qty × (1 + loss %)), each line converted to the
 *     component's base unit, ÷ the batch yield converted to the product's base unit —
 *     components rolled up the same way, however deeply nested;
 *  3. its purchase (source) cost.
 * Exact decimals throughout; whole cents (half up) only at the end. A product whose cost
 * can't be worked out says why (a gap) and is counted in the coverage — never a silent 0.
 * @module
 */

/** Where a standard cost came from (`product.cost_source`). */
export const COST_SOURCES = ['source', 'rollup', 'manual', 'admin'] as const;
/** One of {@link COST_SOURCES}. */
export type CostSource = (typeof COST_SOURCES)[number];

/** Why a product has no standard cost. */
export type StandardCostGap =
  | { code: 'no_cost' }
  | { code: 'recipe_blocked' }
  | { code: 'component_cost_missing'; componentIds: string[] }
  | { code: 'cycle' }
  | { code: 'missing_conversion'; productId: string; from: Uom; to: Uom };

/** One recipe line for costing. */
export interface CostRecipeLine {
  componentId: string;
  /** Per batch, in `uom`. */
  qty: string;
  uom: Uom;
  /** Default `0`. */
  lossPct?: string;
}

/** A product as the roll-up sees it. */
export interface CostInputProduct {
  id: string;
  /** Its base unit. Absent: recipe quantities naming it are taken to be in its base unit. */
  baseUom?: Uom;
  /** A person's cost per base unit: wins over recipe and purchase cost. */
  fixed?: { cents: Cents; source: 'manual' | 'admin' } | null;
  /** The purchase (source system) cost per base unit. */
  purchaseCents?: Cents | null;
  /** The recipe to roll up from (the active version, else the caller's choice). */
  recipe?: { batchYieldQty: string; yieldUom: Uom; lines: readonly CostRecipeLine[] } | null;
  /** The recipe can't be used yet (an unresolved import line). */
  recipeBlocked?: boolean;
}

/** A product's standard cost, or why it has none. */
export type StandardCost =
  | {
      ok: true;
      /** Per base unit, whole cents (half up). */
      cents: Cents;
      /** Per base unit, exact (decimal string) — what parents roll up from. */
      exact: string;
      source: CostSource;
    }
  | { ok: false; gap: StandardCostGap };

/** "Computed for X of Y; missing: …" (lesson 12: coverage, never a silent pass). */
export interface CostCoverage {
  computed: number;
  total: number;
  missing: { productId: string; gap: StandardCostGap }[];
}

/** Result of {@link rollUpStandardCosts}. */
export interface CostRollUp {
  costs: Map<string, StandardCost>;
  coverage: CostCoverage;
}

/** Options for {@link rollUpStandardCosts}. */
export interface CostRollUpOptions {
  conversions?: readonly UomConversion[];
  /** Products to cost and count (default: every product given). */
  targets?: readonly string[];
}

type Unit = { value: Decimal; source: CostSource } | { gap: StandardCostGap };

/** Rolls up standard costs (see the module comment). Deterministic; never throws on data. */
export function rollUpStandardCosts(
  products: Iterable<CostInputProduct>,
  options: CostRollUpOptions = {},
): CostRollUp {
  const byId = new Map<string, CostInputProduct>();
  for (const p of products) byId.set(p.id, p);
  const conversions = options.conversions ?? [];
  const memo = new Map<string, Unit>();
  const visiting = new Set<string>();

  const toBase = (
    amount: Decimal,
    from: Uom,
    productId: string,
  ): { ok: true; value: Decimal } | { ok: false; gap: StandardCostGap } => {
    const base = byId.get(productId)?.baseUom;
    if (base === undefined || base === from) return { ok: true, value: amount };
    const c = convert(qty(amount), from, base, conversions, { productId, dp: 12 });
    return c.ok
      ? { ok: true, value: c.qty }
      : { ok: false, gap: { code: 'missing_conversion', productId, from, to: base } };
  };

  const unit = (id: string): Unit => {
    const known = memo.get(id);
    if (known) return known;
    if (visiting.has(id)) return { gap: { code: 'cycle' } };
    const p = byId.get(id);
    let result: Unit;
    if (!p) {
      result = { gap: { code: 'no_cost' } };
    } else if (p.fixed) {
      result = { value: new D(p.fixed.cents), source: p.fixed.source };
    } else if (p.recipe) {
      result = p.recipeBlocked ? { gap: { code: 'recipe_blocked' } } : fromRecipe(p, p.recipe);
    } else if (p.purchaseCents !== undefined && p.purchaseCents !== null) {
      result = { value: new D(p.purchaseCents), source: 'source' };
    } else {
      result = { gap: { code: 'no_cost' } };
    }
    memo.set(id, result);
    return result;
  };

  const fromRecipe = (p: CostInputProduct, recipe: NonNullable<CostInputProduct['recipe']>) => {
    visiting.add(p.id);
    let total = new D(0);
    const missing: string[] = [];
    let cycle = false;
    let conversionGap: StandardCostGap | null = null;
    for (const line of recipe.lines) {
      const c = unit(line.componentId);
      if ('gap' in c) {
        missing.push(line.componentId);
        cycle ||= c.gap.code === 'cycle';
        continue;
      }
      const gross = new D(line.qty).times(new D(1).plus(new D(line.lossPct ?? '0').div(100)));
      const inBase = toBase(gross, line.uom, line.componentId);
      if (!inBase.ok) {
        conversionGap ??= inBase.gap;
        continue;
      }
      total = total.plus(c.value.times(inBase.value));
    }
    visiting.delete(p.id);
    if (cycle) return { gap: { code: 'cycle' } } as Unit;
    if (missing.length > 0) {
      return {
        gap: { code: 'component_cost_missing', componentIds: [...new Set(missing)].sort() },
      } as Unit;
    }
    if (conversionGap) return { gap: conversionGap } as Unit;
    const yieldInBase = toBase(new D(recipe.batchYieldQty), recipe.yieldUom, p.id);
    if (!yieldInBase.ok) return { gap: yieldInBase.gap } as Unit;
    return { value: total.div(yieldInBase.value), source: 'rollup' } as Unit;
  };

  const targets = options.targets ?? [...byId.keys()];
  const costs = new Map<string, StandardCost>();
  const coverage: CostCoverage = { computed: 0, total: 0, missing: [] };
  for (const id of targets) {
    if (costs.has(id)) continue;
    const u = unit(id);
    coverage.total++;
    if ('gap' in u) {
      costs.set(id, { ok: false, gap: u.gap });
      coverage.missing.push({ productId: id, gap: u.gap });
    } else {
      coverage.computed++;
      costs.set(id, {
        ok: true,
        cents: cents(u.value.toDecimalPlaces(0, D.ROUND_HALF_UP).toNumber()),
        exact: u.value.toDecimalPlaces(12, D.ROUND_HALF_UP).toFixed(),
        source: u.source,
      });
    }
  }
  return { costs, coverage };
}

/** Plain words for a gap (the Admin shows it next to the product). */
export function describeCostGap(gap: StandardCostGap, nameOf: (id: string) => string): string {
  switch (gap.code) {
    case 'no_cost':
      return 'no purchase cost and no recipe';
    case 'recipe_blocked':
      return 'its recipe is waiting for an import decision';
    case 'cycle':
      return 'its recipe uses itself (a loop)';
    case 'component_cost_missing':
      return `no cost for ${gap.componentIds.map(nameOf).join(', ')}`;
    case 'missing_conversion':
      return `no unit conversion from ${gap.from} to ${gap.to} for ${nameOf(gap.productId)}`;
  }
}

/** "Computed for 12 of 14; missing: A (no cost for B), …" — the coverage in one line. */
export function describeCostCoverage(
  coverage: CostCoverage,
  nameOf: (id: string) => string,
  maxListed = 5,
): string {
  const head = `Computed for ${coverage.computed} of ${coverage.total}`;
  if (coverage.missing.length === 0) return `${head}.`;
  const listed = coverage.missing
    .slice(0, maxListed)
    .map((m) => `${nameOf(m.productId)} (${describeCostGap(m.gap, nameOf)})`);
  const more = coverage.missing.length - listed.length;
  return `${head}; missing: ${listed.join(', ')}${more > 0 ? `, and ${more} more` : ''}.`;
}
